import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { theme, Text, Modal, CardAnt } from '@pfmam/pfmam';
import { Box, Container } from '../src/styles/globalStyle';
import AppLayout from './common/layouts/appLayout';
import { getAccInvestmentTitle } from './transactionPages/common/commonFunctions';
import { Row, Form } from 'antd';
import FundInvestmentTypeModal from './transactionPages/common/fundInvestmentTypeModal';
import { InvestmentSetlectStyle } from './activityHistory/components/stopPayments/initiateStopPayments';
import ListWrapperComponent from './common/components/listWrapper';
import { GetWireInstructions, GetFundDetails, GetPhysicalAddress } from './actions/actions';
import { useSelector } from 'react-redux';
const OverrideStyle = createGlobalStyle`
  .ant-card {
    margin-bottom: 0;
    &.basic-card {
      border: 0;
      border-radius: 4px;
    }
    .ant-card-head {
      border-color: ${theme.colors.grey_fossil};
      padding: 0 24px;
      font-family: 'Inter';
      font-weight: normal; 
      .ant-card-head-title {
        font-size: ${theme.font_size.size_2xl};
        color: ${theme.colors.black_tertiary};
        line-height: 30px;
        padding: 16px 0;
      }
    }
    .ant-card-body {
      padding: 24px;
    }
  }

  .ant-card.header-highlight-card {
    .ant-card-head {
      background-color: ${(props) => props.linkTextColor};
    }
  }

  .ant-card.wire-instructions-card {
    .ant-card-head .ant-card-head-title {
      white-space: normal;
      overflow: visible;
      text-overflow: unset;
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const FundCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  padding-top: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const INITIAL_WIRE_INSTRUCTIONS = {
  mvarABANumber: '',
  mvarBankAccountNumber: '',
  mvarBankCity: '',
  mvarBankName: '',
  mvarBankState: '',
  mvarBeneAccountNumber: '',
  mvarBeneDetails: '',
  mvarBeneName: '',
};

const INITIAL_PHYSICAL_ADDRESS = {
  BuildingNumber: '',
  StreetName: '',
  City: '',
  State: '',
  County: '',
  ZipCode: '',
};

const RenderFundWireInstructions = ({ fundUniqueID }) => {
  const [wireInstructions, setWireInstructions] = useState(INITIAL_WIRE_INSTRUCTIONS);

  useEffect(() => {
    if (!fundUniqueID || fundUniqueID === 0) {
      setWireInstructions(INITIAL_WIRE_INSTRUCTIONS);
      return;
    }

    GetWireInstructions(fundUniqueID)
      .then((response) => {
        setWireInstructions(response.data || INITIAL_WIRE_INSTRUCTIONS);
      })
      .catch((error) => {
        console.log(error);
        setWireInstructions(INITIAL_WIRE_INSTRUCTIONS);
      });
  }, [fundUniqueID]);

  return (
    <Row gutter={16}>
      <ListWrapperComponent label="Bank Name" value={wireInstructions.mvarBankName} />
      <ListWrapperComponent
        label="Bank City, State"
        value={[wireInstructions.mvarBankCity, wireInstructions.mvarBankState].filter(Boolean).join(', ')}
      />
      <ListWrapperComponent label="ABA#" value={wireInstructions.mvarABANumber} />
      <ListWrapperComponent
        label="Credit"
        value={wireInstructions.mvarBeneName}
      />
      <ListWrapperComponent label="Account Number" value={wireInstructions.mvarBankAccountNumber} />
      <ListWrapperComponent
        label="Further Credit"
        value={[wireInstructions.mvarBeneDetails, wireInstructions.mvarBeneAccountNumber].filter(Boolean).join(' ')}
      />
    </Row>
  );
};


const RenderFundPhysicalDetails = ({ ProgramID }) => {
  const [fundPhysicalAddress, setFundPhysicalAddress] = useState(INITIAL_PHYSICAL_ADDRESS);

  useEffect(() => {
    if (!ProgramID) {
      setFundPhysicalAddress(INITIAL_PHYSICAL_ADDRESS);
      return;
    }

    GetPhysicalAddress(ProgramID)
      .then((response) => {
        if (response?.data?.Table?.length > 0) {
          setFundPhysicalAddress(response.data.Table[0]);
        } else {
          setFundPhysicalAddress(INITIAL_PHYSICAL_ADDRESS);
        }
      })
      .catch((error) => {
        console.log(error);
        setFundPhysicalAddress(INITIAL_PHYSICAL_ADDRESS);
      });
  }, [ProgramID]);

  return (
    <Row gutter={16}>
      <ListWrapperComponent label="Building Number" value={fundPhysicalAddress.BuildingNumber} />
      <ListWrapperComponent
        label="Street Name"
        value={fundPhysicalAddress.StreetName}
      />
      <ListWrapperComponent label="City" value={fundPhysicalAddress.City} />
      <ListWrapperComponent
        label="State"
        value={fundPhysicalAddress.State}
      />
      <ListWrapperComponent label="County" value={fundPhysicalAddress.County} />
      <ListWrapperComponent
        label="Zip Code"
        value={fundPhysicalAddress.ZipCode}
      />
    </Row>
  );
};
const FundWireInstructions = ({ isAuthenticatedUser }) => {
  const [fundDetails, setFundDetails] = useState({ FundUniqueID: 0, FundShortName: '' });
  const [fundOptions, setFundOptions] = useState([]);
  const [form] = Form.useForm();
  const [showInvestmentTypeModal, setShowInvestmentTypeModal] = useState(false);
  const clientDetails = useSelector(
    (state) => state.programsClientsSlice.selectedClient
  );
  const selectedClient = clientDetails?.payload?.[0] || {};
  const ProgramID = selectedClient.programid;
  const primaryColor = useSelector((state) => state.siteInfo.primaryColor);

  useEffect(() => {
    if (!ProgramID) {
      setFundOptions([]);
      setFundDetails({ FundUniqueID: 0, FundShortName: '' });
      return;
    }

    let isMounted = true;

    GetFundDetails(ProgramID)
      .then((response) => {
        const funds = Array.isArray(response?.data?.Table) ? response.data.Table : [];

        if (!isMounted) return;

        if (funds.length > 0) {
          setFundOptions(funds);
          const defaultAccount = funds[0];
          const defaultInvestmentType = {
            FundUniqueID: defaultAccount.FundUniqueID,
            FundShortName: defaultAccount.FundShortName,
          };

          form.setFieldsValue({
            accountinvestmentType: defaultInvestmentType.FundUniqueID,
          });

          setFundDetails({
            FundUniqueID: defaultInvestmentType.FundUniqueID,
            FundShortName: defaultInvestmentType.FundShortName,
          });
        } else {
          setFundOptions([]);
          setFundDetails({ FundUniqueID: 0, FundShortName: '' });
          form.setFieldsValue({ accountinvestmentType: undefined });
        }
      })
      .catch((error) => {
        console.log(error);
        if (isMounted) {
          setFundOptions([]);
          setFundDetails({ FundUniqueID: 0, FundShortName: '' });
          form.setFieldsValue({ accountinvestmentType: undefined });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [ProgramID, form]);

  const onInvestmentTypeChange = (selectedFundUniqueID) => {
    const selectedFund = fundOptions.find(
      (fund) => fund.FundUniqueID === selectedFundUniqueID
    );

    if (!selectedFund) return;

    form.setFieldsValue({
      accountinvestmentType: selectedFund.FundUniqueID,
    });
    setFundDetails({
      FundUniqueID: selectedFund.FundUniqueID,
      FundShortName: selectedFund.FundShortName,
    });
    setShowInvestmentTypeModal(false);
  };

  return (
    <>
      {isAuthenticatedUser ? (
        <AppLayout>
          <Container>
            <OverrideStyle linkTextColor={primaryColor} />
            <InvestmentSetlectStyle />
            <FundCardsGrid>
              <Wrapper>
                <CardAnt
                  cardContent={
                    <>
                          <p
                            style={{
                              fontSize: '14px',
                              fontStyle: 'normal',
                              color: theme.colors.black_tertiary,
                              margin: '0 0 18px',
                            }}
                          >
                            Provide these instructions to your bank when sending money via wire.
                            <span style={{ display: 'block' }}>Beginning Fall 2026, the Fund's physical address will be required fields for wire instructions.</span>
                          </p>
                      <Form form={form} layout="vertical" requiredMark={false}>
                        <Form.Item
                          label="Investment Type"
                          name="accountinvestmentType"
                          rules={[
                            {
                              required: true,
                              message: 'Investment Type required',
                            },
                          ]}
                          className="investment-input gap-none"
                        >
                          {form.getFieldValue('accountinvestmentType') ? (
                            getAccInvestmentTitle(
                              '',
                              fundDetails.FundShortName,
                              showInvestmentTypeModal,
                              setShowInvestmentTypeModal,
                            )
                          ) : null}
                        </Form.Item>
                      </Form>
                      <RenderFundWireInstructions fundUniqueID={fundDetails.FundUniqueID} />
                    </>
                  }
                  className="profile-card basic-card header-highlight-card wire-instructions-card"
                  title={<Box display="flex" flexdirection="column" width="100%" minwidth="100%">
                      <Text
                        color={`${theme.colors.white}`}
                        fontSize="30px"
                        lineheight="45px"
                        label="Fund Wire Instructions"
                      />
                    </Box>}
                  type="secondary"
                />
              </Wrapper>
              <Wrapper>
                <CardAnt
                  cardContent={<RenderFundPhysicalDetails ProgramID={ProgramID} />}
                  className="profile-card basic-card header-highlight-card"
                  title={<Box display="flex" flexdirection="column" width="100%" minwidth="100%">
                      <Text
                        color={`${theme.colors.white}`}
                        fontSize="30px"
                        lineheight="38px"
                        label="Fund Physical Address"
                      />
                    </Box>}
                  type="secondary"
                />
              </Wrapper>
            </FundCardsGrid>
          </Container>
        </AppLayout>
      ) : null}
      {showInvestmentTypeModal && (
        <Modal
          title="Investment Types"
          width={590}
          bodyStyle={{ padding: '0 0 64px 0' }}
          maskStyle={{ backgroundColor: `${theme.rgba.modal}`, zIndex: '1051' }}
          content={
            <FundInvestmentTypeModal
              onInvestmentTypeChange={onInvestmentTypeChange}
              fundDetails={fundOptions}
              selectedFundUniqueID={fundDetails.FundUniqueID}
            />
          }
          visible={showInvestmentTypeModal}
          onCancel={() => setShowInvestmentTypeModal(false)}
          footer={null}
          centered={true}
        />
      )}

    </>
  );
};

export default FundWireInstructions;
